import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useInView } from 'react-intersection-observer';
import {
    UIEmptyState,
    UIErrorState,
    UIFilterGroup,
    UIFormInput,
    UIIcon,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIMultiFilterGroup,
    UIMultiFilterItem,
    UIPopover,
    UIPressableAreaButtonLess,
    UIPropoverRef,
    UISection,
    UISpinner,
    UITableHeaderCell,
    UITableSortDirection,
    UITooltip,
    useUIConfirmationModal
} from '@/ui';
import styles from './team-users-list.module.scss';
import { CompanyUser } from '@/api';
import { blue, charcoal, red } from '@/ui/styles';
import { useCompanyContext } from '../providers';
import { useFetchUserCounts, useFetchUsersPaginated } from '../queries';
import { useDebouncedValue } from '@/common/hooks';
import { parseSortOrder, setOrDeleteParam } from '@/common/util';
import {
    useAssignLicenseAction,
    useDeleteTeamUserAction,
    useRemoveLicenseAction,
    useResendInviteAction,
    useUpdateTeamRoleAction
} from '../actions';
import { TeamUserRoles } from './team-user-roles';
import { UserInfoListItem } from './user-info-list-item';
import { UpdateTeamRoleModal } from './update-team-role-modal';
import { useCompaniesTranslation } from '../hooks';

type SortBy = 'name' | 'roles' | 'licenses';

type MembershipStatus = 'all' | 'joined' | 'invited';

// Maps the table's sort columns to the API's sortBy values — the Role column
// sorts by team role here, not company role.
const SORT_BY_PARAM: Record<SortBy, 'name' | 'teamRole' | 'license'> = {
    name: 'name',
    roles: 'teamRole',
    licenses: 'license'
};

const TEAM_ROLE_IDS = ['admin', 'guardian', 'memberOnly'];

function parseMembership(value: string | null): MembershipStatus {
    return value === 'joined' || value === 'invited' ? value : 'all';
}

function parseRoles(value: string | null): Map<string, boolean> {
    const roles = new Map<string, boolean>();
    // Anything unrecognised is dropped rather than trusted — the query string
    // is user-editable.
    value?.split(',').forEach((role) => {
        if (TEAM_ROLE_IDS.includes(role)) {
            roles.set(role, true);
        }
    });
    return roles;
}

function serialiseRoles(roles: Map<string, boolean>): string {
    return Array.from(roles.entries())
        .filter(([, selected]) => selected)
        .map(([role]) => role)
        .join(',');
}

function parseSortBy(value: string | null): SortBy {
    return value && value in SORT_BY_PARAM ? (value as SortBy) : 'name';
}

type TeamUsersListProps = {
    teamId: string;
};
export function TeamUsersList(props: TeamUsersListProps): ReactElement {
    const { teamId } = props;

    const { t } = useCompaniesTranslation();

    const { company } = useCompanyContext();
    // Company-wide count — feeds the "no licenses available" guard on the
    // assign-license action, so the table waits for it rather than offering
    // seats it can't yet verify.
    const {
        counts,
        status: countsStatus,
        refetch: refetchCounts
    } = useFetchUserCounts(company.id);

    // Seeded from the query string, then written back to it below. Read once,
    // on mount: after that this component owns the values, and re-reading would
    // fight the user as they type.
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(
        () => searchParams.get('q') ?? ''
    );
    const [membershipFilter, setMembershipFilter] = useState<MembershipStatus>(
        () => parseMembership(searchParams.get('membership'))
    );
    const [rolesFilter, setRolesFilter] = useState<Map<string, boolean>>(() =>
        parseRoles(searchParams.get('roles'))
    );
    const selectedCount = useMemo(() => {
        let count = 0;
        if (membershipFilter !== 'all') {
            count++;
        }

        rolesFilter.forEach((value) => {
            if (value) {
                count++;
            }
        });

        return count;
    }, [membershipFilter, rolesFilter]);

    function clearSearch() {
        setSearchQuery('');
        setMembershipFilter('all');
        setRolesFilter(new Map());
    }

    // The filter callback identifies a group by its (translated) name, so both
    // sides read from here rather than repeating the string.
    const groupNames = useMemo(
        () => ({
            membership: t('shared.members.filter_membership'),
            roles: t('shared.members.filter_roles')
        }),
        [t]
    );

    const groups: UIMultiFilterGroup[] = useMemo(() => {
        const items: UIMultiFilterGroup[] = [
            {
                name: groupNames.membership,
                type: 'single',
                items: [
                    {
                        name: t('shared.members.filter_membership_all'),
                        id: 'all',
                        selected: membershipFilter === 'all'
                    },
                    {
                        name: t('shared.members.filter_membership_joined'),
                        id: 'joined',
                        selected: membershipFilter === 'joined'
                    },
                    {
                        name: t('shared.members.filter_membership_invited'),
                        id: 'invited',
                        selected: membershipFilter === 'invited'
                    }
                ]
            },
            {
                name: groupNames.roles,
                type: 'multi',
                items: [
                    {
                        name: t(
                            'components.team_users_list.filter_team_role_admin'
                        ),
                        id: 'admin',
                        selected: rolesFilter.get('admin') ?? false
                    },
                    {
                        name: t(
                            'components.team_users_list.filter_team_role_guardian'
                        ),
                        id: 'guardian',
                        selected: rolesFilter.get('guardian') ?? false
                    },
                    {
                        name: t(
                            'components.team_users_list.filter_team_role_member'
                        ),
                        id: 'memberOnly',
                        selected: rolesFilter.get('memberOnly') ?? false
                    }
                ]
            }
        ];

        return items;
    }, [membershipFilter, rolesFilter, groupNames, t]);

    const [sortBy, setSortBy] = useState<SortBy>(() =>
        parseSortBy(searchParams.get('sort'))
    );
    const [sortDirection, setSortDirection] = useState<UITableSortDirection>(
        () => parseSortOrder(searchParams.get('order'))
    );

    const debouncedSearch = useDebouncedValue(searchQuery.trim());

    // Mirrored into the query string so opening a member and coming back — or a
    // refresh, or a shared link — lands on the same view. Replaces rather than
    // pushes: a search would otherwise leave a history entry per keystroke. The
    // debounced search is written, not the raw one, so the URL matches the rows
    // actually on screen.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        setOrDeleteParam(next, 'q', debouncedSearch);
        setOrDeleteParam(
            next,
            'membership',
            membershipFilter === 'all' ? '' : membershipFilter
        );
        setOrDeleteParam(next, 'roles', serialiseRoles(rolesFilter));
        setOrDeleteParam(next, 'sort', sortBy === 'name' ? '' : sortBy);
        setOrDeleteParam(
            next,
            'order',
            sortDirection === 'asc' ? '' : sortDirection
        );

        // Guarded: `searchParams` changes identity on every navigation, and
        // setting it unconditionally would navigate in a loop.
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [
        searchParams,
        setSearchParams,
        debouncedSearch,
        membershipFilter,
        rolesFilter,
        sortBy,
        sortDirection
    ]);

    const selectedTeamRoles = useMemo(() => {
        return Array.from(rolesFilter.entries())
            .filter(([, selected]) => selected)
            .map(([role]) => role) as ('admin' | 'guardian' | 'memberOnly')[];
    }, [rolesFilter]);

    const {
        users,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useFetchUsersPaginated({
        companyId: company.id,
        search: debouncedSearch,
        teamIds: [teamId],
        membership: membershipFilter,
        teamRoles: selectedTeamRoles,
        sortBy: SORT_BY_PARAM[sortBy],
        sortOrder: sortDirection
    });

    // The rows and the counts that guard their actions are shown together —
    // rendering rows first would briefly offer actions against unknown limits.
    const listStatus =
        status === 'error' || countsStatus === 'error'
            ? 'error'
            : status === 'pending' || countsStatus === 'pending'
              ? 'pending'
              : 'success';

    const { ref: bottomRef, inView } = useInView({ threshold: 0 });
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Distinguishes "this team has no users" from "no users match the
    // search/filters" now that only the filtered window is loaded.
    const hasActiveFilters =
        debouncedSearch !== '' ||
        membershipFilter !== 'all' ||
        selectedTeamRoles.length > 0;

    const [updateCompanyUserTeamRole, setUpdateCompanyUserTeamRole] =
        useState<CompanyUser | null>(null);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
                <UIFilterGroup>
                    <UIMultiFilterItem
                        icon="sliders"
                        text={t('shared.labels.filter')}
                        groups={groups}
                        selectedCount={selectedCount}
                        onChanged={(newGroups) => {
                            newGroups.forEach((group) => {
                                if (group.name === groupNames.membership) {
                                    const newSelectedMembershipFilter =
                                        newGroups[0].items.find(
                                            (item) => item.selected
                                        )?.id as MembershipStatus;
                                    setMembershipFilter(
                                        newSelectedMembershipFilter
                                    );
                                } else if (group.name === groupNames.roles) {
                                    const newSelectedRoles = new Map<
                                        string,
                                        boolean
                                    >();
                                    group.items.forEach((item) => {
                                        newSelectedRoles.set(
                                            item.id,
                                            item.selected
                                        );
                                    });
                                    setRolesFilter(newSelectedRoles);
                                }
                            });
                        }}
                    />
                </UIFilterGroup>
                <UIFormInput
                    value={searchQuery}
                    placeholder={t('shared.labels.search')}
                    height={20}
                    onChange={setSearchQuery}
                />
            </div>
            {listStatus === 'pending' && <UILoadingState />}
            {listStatus === 'error' && (
                <UIErrorState
                    title={t('components.team_users_list.load_error_team')}
                    onRetry={() => {
                        refetch();
                        refetchCounts();
                    }}
                />
            )}
            {listStatus === 'success' &&
                users.length === 0 &&
                hasActiveFilters && (
                    <UIEmptyState
                        description={t('shared.labels.no_results')}
                        buttonText={t('shared.labels.clear_search')}
                        emoji="😔"
                        onClick={clearSearch}
                    />
                )}
            {listStatus === 'success' &&
                users.length === 0 &&
                !hasActiveFilters && (
                    <UIEmptyState
                        description={t(
                            'components.team_users_list.no_team_members'
                        )}
                        emoji="😔"
                    />
                )}
            {listStatus === 'success' && users.length > 0 && (
                <UISection>
                    <table className={styles.userTable}>
                        <thead>
                            <UserTableHeader
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                onClick={(newSortBy, newSortDirection) => {
                                    setSortBy(newSortBy);
                                    setSortDirection(newSortDirection);
                                }}
                            />
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <UserTableRow
                                    key={user.id}
                                    user={user}
                                    teamId={teamId}
                                    // Counts have loaded by the time this
                                    // branch renders — the fallback only
                                    // satisfies the type.
                                    assignedLicenses={
                                        counts?.assignedLicense ?? 0
                                    }
                                    onUpdateTeamRole={
                                        setUpdateCompanyUserTeamRole
                                    }
                                />
                            ))}
                            {hasNextPage && (
                                <tr ref={bottomRef}>
                                    <td
                                        colSpan={4}
                                        style={{
                                            textAlign: 'center',
                                            padding: 8
                                        }}
                                    >
                                        {isFetchingNextPage && (
                                            <UISpinner color={charcoal} />
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </UISection>
            )}
            <UpdateTeamRoleModal
                companyUser={updateCompanyUserTeamRole}
                teamId={teamId}
                onClose={() => setUpdateCompanyUserTeamRole(null)}
            />
        </div>
    );
}

function UserTableHeader(props: {
    sortBy: SortBy;
    sortDirection: UITableSortDirection;
    onClick: (sortBy: SortBy, sortDirection: UITableSortDirection) => void;
}): ReactElement {
    const { sortBy, sortDirection, onClick } = props;

    const { t } = useCompaniesTranslation();

    return (
        <tr>
            <UITableHeaderCell
                text={t('shared.labels.name')}
                sortable
                sortDirection={sortBy === 'name' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('name', newSortDirection);
                }}
            />
            <UITableHeaderCell
                text={t('shared.members.column_role')}
                tooltip={t('shared.members.column_role_tooltip')}
                sortable
                sortDirection={sortBy === 'roles' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('roles', newSortDirection);
                }}
            />
            <UITableHeaderCell
                text={t('shared.members.column_license')}
                tooltip={t('shared.members.column_license_tooltip')}
                sortable
                sortDirection={
                    sortBy === 'licenses' ? sortDirection : undefined
                }
                onClick={(newSortDirection) => {
                    onClick('licenses', newSortDirection);
                }}
            />
            <th />
        </tr>
    );
}

function UserTableRow(props: {
    user: CompanyUser;
    teamId: string;
    assignedLicenses: number;
    onUpdateTeamRole: (user: CompanyUser) => void;
}): ReactElement {
    const { user, teamId, assignedLicenses, onUpdateTeamRole } = props;

    const { t } = useCompaniesTranslation();

    const popoverRef = useRef<UIPropoverRef>(null);
    const navigate = useNavigate();

    const deleteUserAction = useDeleteTeamUserAction({ user, teamId });
    const assignLicenseAction = useAssignLicenseAction({
        user,
        assignedLicenses
    });
    const removeLicenseAction = useRemoveLicenseAction({
        user
    });
    const resendInviteAction = useResendInviteAction({
        user
    });
    const updateTeamRoleAction = useUpdateTeamRoleAction({ user, teamId });

    const confirmationModal = useUIConfirmationModal();

    // Every viewer of this list can open a member's profile, as they can from
    // the company members list. What they are shown there is decided by the
    // server, per member.
    return (
        <tr
            className={styles.tableRow}
            onClick={() => {
                void navigate(
                    `/company/${user.companyId}/settings/members/${user.id}`
                );
            }}
        >
            <td>
                <UserInfoListItem user={user} />
            </td>
            <td>
                <RolesCell user={user} teamId={teamId} />
            </td>
            <td>
                <LicenseCell user={user} />
            </td>
            <td onClick={(e) => e.stopPropagation()}>
                <UIPopover
                    ref={popoverRef}
                    placement="auto"
                    popoverPadding={4}
                    trigger="click"
                    triggerNode={
                        <button className={styles.removeButtonStyle}>
                            <UIPressableAreaButtonLess width={16} height={16}>
                                <UIIcon
                                    name={'more-vertical'}
                                    size={16}
                                    color={charcoal}
                                />
                            </UIPressableAreaButtonLess>
                        </button>
                    }
                >
                    <UIMenu>
                        <UIMenuItem
                            text={t('shared.members.action_update_role')}
                            leftIconName="edit-2"
                            loading={updateTeamRoleAction.loading}
                            disabled={!updateTeamRoleAction.isAllowed}
                            disabledTooltip={updateTeamRoleAction.reason}
                            onClick={(e) => {
                                e.stopPropagation();
                                onUpdateTeamRole(user);
                                popoverRef.current?.close();
                            }}
                        />

                        {!user.joined && (
                            <UIMenuItem
                                text={t('shared.members.action_resend_invite')}
                                leftIconName="send"
                                loading={resendInviteAction.loading}
                                disabled={!resendInviteAction.isAllowed}
                                disabledTooltip={resendInviteAction.reason}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    resendInviteAction
                                        .resendInvite()
                                        .catch(() => {}) // Error handled in hook
                                        .finally(() => {
                                            popoverRef.current?.close();
                                        });
                                }}
                            />
                        )}

                        {user.license ? (
                            <UIMenuItem
                                text={t('shared.members.action_remove_license')}
                                leftIconName="x"
                                textColor={red}
                                loading={removeLicenseAction.loading}
                                disabled={!removeLicenseAction.isAllowed}
                                disabledTooltip={removeLicenseAction.reason}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    popoverRef.current?.close();
                                    confirmationModal.open({
                                        title: t(
                                            'shared.members.remove_license_confirm_title'
                                        ),
                                        confirmText: t(
                                            'shared.members.remove_license_confirm_button'
                                        ),
                                        confirmVariant: 'danger',
                                        cancelText: t('shared.labels.cancel'),
                                        onConfirm: () => {
                                            confirmationModal.setLoading(true);
                                            removeLicenseAction
                                                .removeLicense()
                                                .catch(() => {}) // Error handled in hook
                                                .finally(() => {
                                                    confirmationModal.setLoading(
                                                        false
                                                    );
                                                    confirmationModal.close();
                                                });
                                        },
                                        onCancel: () => {
                                            confirmationModal.close();
                                        }
                                    });
                                }}
                            />
                        ) : (
                            <UIMenuItem
                                text={t('shared.members.action_assign_license')}
                                leftIconName="check"
                                loading={assignLicenseAction.loading}
                                disabled={!assignLicenseAction.isAllowed}
                                disabledTooltip={assignLicenseAction.reason}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    assignLicenseAction
                                        .assignLicense()
                                        .catch(() => {}) // Error handled in hook
                                        .finally(() => {
                                            popoverRef.current?.close();
                                        });
                                }}
                            />
                        )}

                        <UIMenuItem
                            text={t(
                                'components.team_users_list.action_remove_from_team'
                            )}
                            leftIconName="trash-2"
                            textColor={red}
                            loading={deleteUserAction.loading}
                            disabled={!deleteUserAction.isAllowed}
                            disabledTooltip={deleteUserAction.reason}
                            onClick={(e) => {
                                e.stopPropagation();
                                popoverRef.current?.close();
                                confirmationModal.open({
                                    title: t(
                                        'components.team_users_list.remove_team_member_confirm_title'
                                    ),
                                    confirmText: t(
                                        'shared.members.remove_member_confirm_button'
                                    ),
                                    confirmVariant: 'danger',
                                    cancelText: t('shared.labels.cancel'),
                                    onConfirm: () => {
                                        confirmationModal.setLoading(true);
                                        deleteUserAction
                                            .deleteTeamUser()
                                            .catch(() => {}) // Error handled in hook
                                            .finally(() => {
                                                confirmationModal.close();
                                            });
                                    },
                                    onCancel: () => {
                                        confirmationModal.close();
                                    }
                                });
                            }}
                        />
                    </UIMenu>
                </UIPopover>
            </td>
        </tr>
    );
}

/**
 * Cells
 */

function RolesCell(props: { user: CompanyUser; teamId: string }): ReactElement {
    const { user, teamId } = props;

    const teamUser = useMemo(() => {
        return user.teams.find((team) => team.teamId === teamId);
    }, [user, teamId]);

    return teamUser ? <TeamUserRoles roleData={teamUser.roles} /> : <></>;
}

function LicenseCell(props: { user: CompanyUser }): ReactElement {
    const { user } = props;

    const { t } = useCompaniesTranslation();

    const hasLicense = !!user.license;
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}
        >
            <UITooltip
                text={
                    hasLicense
                        ? t('shared.members.license_assigned')
                        : t('shared.members.license_not_assigned')
                }
                place="top"
            >
                <UIIcon
                    name={hasLicense ? 'check' : 'x'}
                    color={hasLicense ? blue : red}
                />
            </UITooltip>
        </div>
    );
}
